import Fuse from "fuse.js";
import { useCallback, useMemo, useState } from "react";
import { Text, Input, VStack } from "@chakra-ui/react";
import { InputGroup } from "@/components/ui/input-group";
import { preordersToAlertsCount } from "@/shared/lib/misc";
import { rememberActiveCompany } from "@/app/auth/helpers";
import { usePendingOrdersQuery } from "@/features/order/get";
import { useCreateOrderForm } from "@/features/order/create";
import { LuBriefcaseBusiness, LuSearch } from "react-icons/lu";

import type { Company } from "@entities/company";
import { MenuItemButton } from "@shared/ui/menu-item-button";
import { useCompaniesSource } from "../hooks/use-companies-source";
import { MANY_COMPANIES_THRESHOLD } from "@entities/order/model/constants";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";

export const CompanyContextSwitch: React.FC = () => {
  const [query, setQuery] = useState<string>("");
  const { values } = useCreateOrderForm();
  const { user, activeCompany, setActiveCompany } = useAuthenticationContext();
  const { data: pendingPreorders } = usePendingOrdersQuery({
    filters: { pageSize: 100, page: 1 },
  });
  const { companies } = useCompaniesSource();

  const onCompanySwitch = useCallback(
    (company: Company) => {
      if (company.id === activeCompany?.id) return;

      const isConfirmed =
        values?.lines?.length > 0
          ? window.confirm(`This will clear your cart. Continue?`)
          : true;

      if (!isConfirmed) return;

      setActiveCompany(company);
      rememberActiveCompany(company);
    },
    [activeCompany, setActiveCompany, values.lines],
  );

  const companiesFuse = useMemo<Fuse<Company>>(
    () =>
      new Fuse<Company>(companies, {
        isCaseSensitive: false,
        keys: ["name"],
        includeScore: false,
      }),
    [companies],
  );

  const companyMatches = useMemo<Company[]>(
    () =>
      query ? companiesFuse.search(query).map((hit) => hit.item) : companies,
    [companiesFuse, companies, query],
  );

  const alerts = useMemo(
    () => preordersToAlertsCount(pendingPreorders?.rows || []),
    [pendingPreorders?.rows],
  );

  return (
    <>
      {user?.isSuperuser && (
        <Text fontSize="xs" color="fg.muted" w="full">
          Click on a company to perform actions on behalf of its manager.
        </Text>
      )}
      {companies.length > MANY_COMPANIES_THRESHOLD && (
        <InputGroup flex="1" w="full" startElement={<LuSearch />}>
          <Input
            size="xs"
            value={query}
            autoComplete="one-time-code"
            placeholder="Search companies"
            onChange={(e) => setQuery(e.target.value)}
          />
        </InputGroup>
      )}
      <VStack
        w="full"
        maxH="14rem"
        overflowY={
          companyMatches.length > MANY_COMPANIES_THRESHOLD ? "auto" : "hidden"
        }
        scrollbarWidth="thin"
      >
        {companyMatches.map((company) => (
          <MenuItemButton
            key={company.id}
            label={company.name}
            icon={LuBriefcaseBusiness}
            active={company.id === activeCompany?.id}
            notificationText={alerts[company.serviceId!]}
            onClick={() => onCompanySwitch(company)}
          />
        ))}
      </VStack>
    </>
  );
};
